import React from "react";

/**
 * Hand-drawn 2D anatomical vector diagrams — one per movement pattern.
 *
 * Every figure is a side-view stick-figure showing the CONCENTRIC-peak
 * position of the movement so the client instantly sees the shape.
 *
 *  Design rules
 *  ------------
 *  - viewBox 0 0 100 100 (so we can slot into any square/hero tile)
 *  - Lines use `currentColor` → the surrounding component controls tint
 *  - strokeWidth 2.5 for body, 3 for the bar / weight for extra weight
 *  - Head = circle r=5; joints = tiny circles r=1.5 for readability
 *  - Ground line where relevant
 *
 * Adding a new pattern:
 *   1. Add a component below
 *   2. Register it in PATTERNS
 *   3. Map at least one exercise id in EXERCISE_PATTERN
 */

const COMMON = {
  strokeWidth: 2.4,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  fill: "none",
};

const Ground = ({ y = 92 }) => (
  <line x1="8" y1={y} x2="92" y2={y} stroke="currentColor" strokeWidth="1" strokeDasharray="2 3" opacity="0.4" />
);
const Head = ({ cx, cy }) => (
  <circle cx={cx} cy={cy} r="5" stroke="currentColor" strokeWidth="2.2" fill="none" />
);
const Plate = ({ cx, cy, r = 4 }) => (
  <circle cx={cx} cy={cy} r={r} stroke="currentColor" strokeWidth="2" fill="none" />
);

// ---------------------------------------------------------------------------
// Individual movement diagrams
// ---------------------------------------------------------------------------

// Front-view squat — bar on shoulders, deep sit
const SquatSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Bar across shoulders */}
      <line x1="20" y1="30" x2="80" y2="30" strokeWidth="3" />
      <Plate cx={20} cy={30} />
      <Plate cx={80} cy={30} />
      <Head cx={50} cy={20} />
      {/* Torso */}
      <line x1="50" y1="25" x2="50" y2="52" />
      {/* Deep squat legs — hips low */}
      <line x1="50" y1="52" x2="34" y2="70" />
      <line x1="34" y1="70" x2="34" y2="90" />
      <line x1="50" y1="52" x2="66" y2="70" />
      <line x1="66" y1="70" x2="66" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Side hinge — deadlift / RDL / KB swing base
const HingeSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={22} cy={30} />
      {/* Torso hinged forward */}
      <line x1="26" y1="32" x2="58" y2="52" />
      {/* Hips → knee */}
      <line x1="58" y1="52" x2="62" y2="72" />
      {/* Shin */}
      <line x1="62" y1="72" x2="62" y2="90" />
      {/* Arm hanging with bar */}
      <line x1="52" y1="48" x2="52" y2="78" />
      <line x1="30" y1="78" x2="82" y2="78" strokeWidth="3" />
      <Plate cx={30} cy={78} />
      <Plate cx={82} cy={78} />
      <Ground />
    </g>
  </svg>
);

// Barbell bench press — lying, arms up
const BenchPressSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Bench */}
      <rect x="18" y="60" width="55" height="4" fill="none" strokeWidth="2" />
      <line x1="26" y1="64" x2="26" y2="82" />
      <line x1="65" y1="64" x2="65" y2="82" />
      {/* Body lying */}
      <Head cx={22} cy={52} />
      <line x1="26" y1="56" x2="72" y2="58" />
      {/* Legs off bench, knees bent */}
      <line x1="72" y1="58" x2="82" y2="70" />
      <line x1="82" y1="70" x2="82" y2="88" />
      {/* Arms pressing up */}
      <line x1="40" y1="58" x2="40" y2="34" />
      <line x1="55" y1="58" x2="55" y2="34" />
      {/* Bar */}
      <line x1="28" y1="32" x2="68" y2="32" strokeWidth="3" />
      <Plate cx={28} cy={32} r={3.5} />
      <Plate cx={68} cy={32} r={3.5} />
      <Ground />
    </g>
  </svg>
);

// Push-up — side view, elbows bent
const PushUpSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={18} cy={58} />
      {/* Straight body from head to feet */}
      <line x1="22" y1="60" x2="88" y2="76" />
      {/* Arms bent — elbow near shoulder line */}
      <line x1="30" y1="62" x2="34" y2="78" />
      <line x1="34" y1="78" x2="30" y2="86" />
      {/* Feet */}
      <line x1="88" y1="76" x2="92" y2="88" />
      <Ground />
    </g>
  </svg>
);

// Overhead press — standing, arms up
const OverheadPressSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={50} cy={30} />
      {/* Torso */}
      <line x1="50" y1="35" x2="50" y2="68" />
      {/* Arms extending straight up */}
      <line x1="50" y1="35" x2="34" y2="18" />
      <line x1="50" y1="35" x2="66" y2="18" />
      {/* Bar overhead */}
      <line x1="24" y1="14" x2="76" y2="14" strokeWidth="3" />
      <Plate cx={24} cy={14} />
      <Plate cx={76} cy={14} />
      {/* Legs */}
      <line x1="50" y1="68" x2="42" y2="90" />
      <line x1="50" y1="68" x2="58" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Bent-over row — hinged torso, arms pulling in
const RowSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={22} cy={30} />
      {/* Hinged torso */}
      <line x1="26" y1="34" x2="58" y2="50" />
      {/* Elbow pulled back / arm to bar */}
      <line x1="46" y1="48" x2="50" y2="66" />
      {/* Bar */}
      <line x1="28" y1="68" x2="72" y2="68" strokeWidth="3" />
      <Plate cx={28} cy={68} />
      <Plate cx={72} cy={68} />
      {/* Legs bent */}
      <line x1="58" y1="50" x2="60" y2="72" />
      <line x1="60" y1="72" x2="60" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Pull-up — hanging, arms up to bar
const PullUpSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Bar */}
      <line x1="16" y1="14" x2="84" y2="14" strokeWidth="3" />
      <line x1="16" y1="10" x2="16" y2="18" />
      <line x1="84" y1="10" x2="84" y2="18" />
      <Head cx={50} cy={30} />
      {/* Arms up to bar */}
      <line x1="42" y1="30" x2="34" y2="14" />
      <line x1="58" y1="30" x2="66" y2="14" />
      {/* Torso */}
      <line x1="50" y1="35" x2="50" y2="70" />
      {/* Legs slightly bent */}
      <line x1="50" y1="70" x2="45" y2="88" />
      <line x1="50" y1="70" x2="55" y2="88" />
    </g>
  </svg>
);

// Lunge — staggered stance side view
const LungeSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={44} cy={22} />
      <line x1="44" y1="27" x2="44" y2="58" />
      {/* Front leg — knee stacked */}
      <line x1="44" y1="58" x2="62" y2="74" />
      <line x1="62" y1="74" x2="62" y2="90" />
      {/* Back leg — extended and low */}
      <line x1="44" y1="58" x2="24" y2="82" />
      <line x1="24" y1="82" x2="14" y2="90" />
      {/* Dumbbells at sides */}
      <rect x="35" y="52" width="4" height="8" fill="none" strokeWidth="2" />
      <rect x="49" y="52" width="4" height="8" fill="none" strokeWidth="2" />
      <Ground />
    </g>
  </svg>
);

// Plank — forearms down, straight body
const PlankSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={20} cy={58} />
      {/* Straight body */}
      <line x1="24" y1="62" x2="88" y2="72" />
      {/* Forearm on ground */}
      <line x1="16" y1="70" x2="30" y2="70" strokeWidth="3" />
      <line x1="26" y1="62" x2="24" y2="70" />
      {/* Feet */}
      <line x1="88" y1="72" x2="94" y2="86" />
      <Ground />
    </g>
  </svg>
);

// Curl — elbow flexion
const CurlSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={40} cy={22} />
      <line x1="40" y1="27" x2="40" y2="70" />
      {/* Upper arm hangs, forearm curled up */}
      <line x1="40" y1="32" x2="40" y2="55" />
      <line x1="40" y1="55" x2="62" y2="30" />
      {/* Dumbbell in hand */}
      <rect x="60" y="24" width="10" height="4" fill="none" strokeWidth="2" />
      <rect x="60" y="30" width="10" height="4" fill="none" strokeWidth="2" />
      {/* Legs */}
      <line x1="40" y1="70" x2="32" y2="90" />
      <line x1="40" y1="70" x2="48" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Tricep extension — elbow overhead, forearm up
const TricepExtSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={44} cy={30} />
      <line x1="44" y1="35" x2="44" y2="72" />
      {/* Upper arm vertical up */}
      <line x1="44" y1="35" x2="44" y2="16" />
      {/* Forearm extended, dumbbell at top */}
      <line x1="44" y1="16" x2="62" y2="24" />
      <rect x="60" y="20" width="10" height="4" fill="none" strokeWidth="2" />
      <rect x="60" y="26" width="10" height="4" fill="none" strokeWidth="2" />
      {/* Legs */}
      <line x1="44" y1="72" x2="36" y2="90" />
      <line x1="44" y1="72" x2="52" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Lateral raise — arms out to sides
const LateralRaiseSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={50} cy={24} />
      <line x1="50" y1="29" x2="50" y2="70" />
      {/* Arms out to sides */}
      <line x1="50" y1="34" x2="24" y2="34" />
      <line x1="50" y1="34" x2="76" y2="34" />
      {/* Dumbbells at each hand */}
      <rect x="14" y="30" width="10" height="4" fill="none" strokeWidth="2" />
      <rect x="14" y="36" width="10" height="4" fill="none" strokeWidth="2" />
      <rect x="76" y="30" width="10" height="4" fill="none" strokeWidth="2" />
      <rect x="76" y="36" width="10" height="4" fill="none" strokeWidth="2" />
      {/* Legs */}
      <line x1="50" y1="70" x2="42" y2="90" />
      <line x1="50" y1="70" x2="58" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Crunch — lying curled up
const CrunchSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Bent legs on ground */}
      <line x1="86" y1="86" x2="72" y2="70" />
      <line x1="72" y1="70" x2="58" y2="80" />
      {/* Torso curled up */}
      <line x1="58" y1="80" x2="34" y2="60" />
      <Head cx={30} cy={56} />
      {/* Arms crossed on chest */}
      <line x1="38" y1="62" x2="48" y2="72" />
      <Ground />
    </g>
  </svg>
);

// Calf raise — on toes
const CalfRaiseSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={50} cy={22} />
      <line x1="50" y1="27" x2="50" y2="70" />
      {/* Legs on toes — heels lifted */}
      <line x1="50" y1="70" x2="43" y2="86" />
      <line x1="43" y1="86" x2="47" y2="92" />
      <line x1="50" y1="70" x2="57" y2="86" />
      <line x1="57" y1="86" x2="53" y2="92" />
      {/* Up arrows on the sides */}
      <line x1="20" y1="60" x2="20" y2="40" strokeWidth="2" />
      <polyline points="16,44 20,40 24,44" strokeWidth="2" />
      <line x1="80" y1="60" x2="80" y2="40" strokeWidth="2" />
      <polyline points="76,44 80,40 84,44" strokeWidth="2" />
      <Ground />
    </g>
  </svg>
);

// Leg extension — seated, leg out
const LegExtSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Seat + backrest */}
      <rect x="16" y="56" width="30" height="4" fill="none" strokeWidth="2" />
      <line x1="16" y1="56" x2="16" y2="30" />
      <Head cx={22} cy={26} />
      {/* Torso against backrest */}
      <line x1="22" y1="31" x2="22" y2="56" />
      {/* Thigh horizontal, shin extended straight out */}
      <line x1="22" y1="56" x2="52" y2="56" />
      <line x1="52" y1="56" x2="78" y2="50" strokeWidth="3" />
      {/* Weight pad */}
      <rect x="72" y="46" width="10" height="6" fill="none" strokeWidth="2" />
      <Ground />
    </g>
  </svg>
);

// Leg curl — prone, leg flexed
const LegCurlSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Bench */}
      <rect x="18" y="60" width="55" height="4" fill="none" strokeWidth="2" />
      {/* Body prone */}
      <Head cx={22} cy={52} />
      <line x1="26" y1="56" x2="70" y2="58" />
      {/* Thigh straight, shin curled up */}
      <line x1="70" y1="58" x2="70" y2="46" />
      <line x1="70" y1="46" x2="86" y2="46" strokeWidth="3" />
      <rect x="80" y="42" width="10" height="6" fill="none" strokeWidth="2" />
      <Ground />
    </g>
  </svg>
);

// KB swing — swinging kettlebell forward at hip level
const KbSwingSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={30} cy={26} />
      {/* Hinged posture */}
      <line x1="30" y1="31" x2="52" y2="50" />
      {/* Arms straight forward with KB */}
      <line x1="48" y1="48" x2="80" y2="50" />
      <circle cx="82" cy="52" r="6" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="M 78 46 Q 82 40 86 46" stroke="currentColor" strokeWidth="2" fill="none" />
      {/* Legs */}
      <line x1="52" y1="50" x2="56" y2="74" />
      <line x1="56" y1="74" x2="56" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Hip thrust — shoulders on bench, hips extended
const HipThrustSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Bench */}
      <rect x="14" y="46" width="22" height="4" fill="none" strokeWidth="2" />
      <Head cx={22} cy={40} />
      {/* Torso up in bridge */}
      <line x1="26" y1="46" x2="50" y2="52" />
      {/* Bar on hips */}
      <line x1="32" y1="52" x2="70" y2="52" strokeWidth="3" />
      <Plate cx={32} cy={52} />
      <Plate cx={70} cy={52} />
      {/* Bent legs */}
      <line x1="50" y1="54" x2="62" y2="76" />
      <line x1="62" y1="76" x2="62" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Leg press — reclined, legs push a plate
const LegPressSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Seat (reclined) */}
      <line x1="12" y1="72" x2="42" y2="60" strokeWidth="2" />
      <line x1="12" y1="72" x2="12" y2="50" />
      <Head cx={16} cy={46} />
      <line x1="20" y1="52" x2="42" y2="60" />
      {/* Legs pressing plate up */}
      <line x1="42" y1="60" x2="66" y2="42" />
      <line x1="66" y1="42" x2="82" y2="30" />
      {/* Plate/foot pad */}
      <rect x="76" y="22" width="12" height="16" fill="none" strokeWidth="2" />
      <Ground />
    </g>
  </svg>
);

// Hanging leg raise — hanging from bar with knees up
const HangLegRaiseSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Bar */}
      <line x1="16" y1="14" x2="84" y2="14" strokeWidth="3" />
      <line x1="16" y1="10" x2="16" y2="18" />
      <line x1="84" y1="10" x2="84" y2="18" />
      <Head cx={50} cy={28} />
      <line x1="42" y1="26" x2="34" y2="14" />
      <line x1="58" y1="26" x2="66" y2="14" />
      {/* Torso */}
      <line x1="50" y1="33" x2="50" y2="60" />
      {/* Knees raised horizontally */}
      <line x1="50" y1="60" x2="76" y2="60" />
      {/* Shins hang */}
      <line x1="76" y1="60" x2="76" y2="82" />
    </g>
  </svg>
);

// Dip — supported, elbows bent below rings/bars
const DipSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Parallel bars */}
      <line x1="20" y1="30" x2="20" y2="55" strokeWidth="3" />
      <line x1="80" y1="30" x2="80" y2="55" strokeWidth="3" />
      <line x1="15" y1="30" x2="30" y2="30" strokeWidth="2" />
      <line x1="70" y1="30" x2="85" y2="30" strokeWidth="2" />
      <Head cx={50} cy={30} />
      {/* Arms bent — shoulders below elbows a bit */}
      <line x1="30" y1="32" x2="42" y2="34" />
      <line x1="70" y1="32" x2="58" y2="34" />
      {/* Torso */}
      <line x1="50" y1="35" x2="50" y2="66" />
      {/* Legs bent up */}
      <line x1="50" y1="66" x2="42" y2="82" />
      <line x1="50" y1="66" x2="58" y2="82" />
    </g>
  </svg>
);

// Twist / rotational (russian twist)
const TwistSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Bent legs raised */}
      <line x1="70" y1="86" x2="60" y2="70" />
      <line x1="60" y1="70" x2="46" y2="74" />
      {/* Torso reclined */}
      <line x1="46" y1="74" x2="30" y2="60" />
      <Head cx={26} cy={56} />
      {/* Arms holding weight to one side */}
      <line x1="34" y1="62" x2="52" y2="70" />
      <rect x="52" y="66" width="10" height="4" fill="none" strokeWidth="2" />
      <rect x="52" y="72" width="10" height="4" fill="none" strokeWidth="2" />
      <Ground />
    </g>
  </svg>
);

// Ab wheel — rollout position
const AbWheelSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Knees on ground */}
      <line x1="18" y1="86" x2="30" y2="70" />
      <line x1="30" y1="70" x2="50" y2="66" />
      <Head cx={54} cy={54} />
      <line x1="58" y1="58" x2="72" y2="70" />
      {/* Wheel */}
      <circle cx="82" cy="80" r="8" stroke="currentColor" strokeWidth="2.5" fill="none" />
      <line x1="72" y1="70" x2="82" y2="80" />
      <Ground />
    </g>
  </svg>
);

// Face pull / rear-delt cable — pulling handles to face
const FacePullSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={50} cy={28} />
      <line x1="50" y1="33" x2="50" y2="68" />
      {/* Arms up and elbows flared */}
      <line x1="50" y1="34" x2="30" y2="30" />
      <line x1="50" y1="34" x2="70" y2="30" />
      <line x1="30" y1="30" x2="24" y2="20" />
      <line x1="70" y1="30" x2="76" y2="20" />
      {/* Cable/rope */}
      <line x1="24" y1="20" x2="14" y2="12" strokeWidth="2" strokeDasharray="3 2" />
      <line x1="76" y1="20" x2="86" y2="12" strokeWidth="2" strokeDasharray="3 2" />
      {/* Legs */}
      <line x1="50" y1="68" x2="42" y2="90" />
      <line x1="50" y1="68" x2="58" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Cable crossover / chest fly — arms swept across chest
const FlySVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={50} cy={26} />
      <line x1="50" y1="31" x2="50" y2="68" />
      {/* Arms swept forward and down (converged) */}
      <line x1="50" y1="34" x2="34" y2="46" />
      <line x1="50" y1="34" x2="66" y2="46" />
      <line x1="34" y1="46" x2="46" y2="54" />
      <line x1="66" y1="46" x2="54" y2="54" />
      {/* Legs */}
      <line x1="50" y1="68" x2="42" y2="90" />
      <line x1="50" y1="68" x2="58" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Lat pulldown — seated, arms wide pulling bar to chest
const LatPulldownSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      {/* Bar overhead */}
      <line x1="18" y1="18" x2="82" y2="18" strokeWidth="3" />
      {/* Cable up */}
      <line x1="50" y1="18" x2="50" y2="8" strokeWidth="2" strokeDasharray="3 2" />
      <Head cx={50} cy={40} />
      <line x1="50" y1="45" x2="50" y2="75" />
      {/* Arms up wide */}
      <line x1="50" y1="46" x2="30" y2="30" />
      <line x1="50" y1="46" x2="70" y2="30" />
      <line x1="30" y1="30" x2="30" y2="18" />
      <line x1="70" y1="30" x2="70" y2="18" />
      {/* Seated legs */}
      <line x1="50" y1="75" x2="70" y2="80" />
      <line x1="70" y1="80" x2="70" y2="90" />
      <Ground />
    </g>
  </svg>
);

// Generic / default — neutral standing figure so we never render nothing
const StandingSVG = (p) => (
  <svg viewBox="0 0 100 100" {...p}>
    <g stroke="currentColor" {...COMMON}>
      <Head cx={50} cy={24} />
      <line x1="50" y1="29" x2="50" y2="70" />
      <line x1="50" y1="34" x2="34" y2="52" />
      <line x1="50" y1="34" x2="66" y2="52" />
      <line x1="50" y1="70" x2="42" y2="90" />
      <line x1="50" y1="70" x2="58" y2="90" />
      <Ground />
    </g>
  </svg>
);

// ---------------------------------------------------------------------------
// Registry: pattern id → SVG component
// ---------------------------------------------------------------------------
const PATTERNS = {
  squat: SquatSVG,
  hinge: HingeSVG,
  "bench-press": BenchPressSVG,
  "push-up": PushUpSVG,
  "overhead-press": OverheadPressSVG,
  row: RowSVG,
  "pull-up": PullUpSVG,
  "lat-pulldown": LatPulldownSVG,
  lunge: LungeSVG,
  plank: PlankSVG,
  curl: CurlSVG,
  "tricep-ext": TricepExtSVG,
  "lateral-raise": LateralRaiseSVG,
  fly: FlySVG,
  "face-pull": FacePullSVG,
  crunch: CrunchSVG,
  twist: TwistSVG,
  "ab-wheel": AbWheelSVG,
  "calf-raise": CalfRaiseSVG,
  "leg-ext": LegExtSVG,
  "leg-curl": LegCurlSVG,
  "kb-swing": KbSwingSVG,
  "hip-thrust": HipThrustSVG,
  "leg-press": LegPressSVG,
  "hang-knee": HangLegRaiseSVG,
  dip: DipSVG,
  standing: StandingSVG,
};

// ---------------------------------------------------------------------------
// Exercise id → pattern id.  Fallback to name/muscle heuristics for anything
// added to the DB in the future.
// ---------------------------------------------------------------------------
const EXERCISE_PATTERN = {
  "barbell-back-squat": "squat",
  "goblet-squat": "squat",
  "split-squat": "lunge",
  "walking-lunge": "lunge",
  "leg-press": "leg-press",

  "conventional-deadlift": "hinge",
  "romanian-deadlift": "hinge",
  "kettlebell-swing": "kb-swing",
  "hip-thrust": "hip-thrust",

  "barbell-bench-press": "bench-press",
  "dumbbell-bench-press": "bench-press",
  "incline-db-press": "bench-press",
  "push-up": "push-up",
  dip: "dip",

  "overhead-press": "overhead-press",
  "dumbbell-overhead-press": "overhead-press",

  "barbell-row": "row",
  "seated-row": "row",
  "lat-pulldown": "lat-pulldown",
  "pull-up": "pull-up",

  "lateral-raise": "lateral-raise",
  "rear-delt-fly": "lateral-raise",
  "face-pull": "face-pull",
  "cable-crossover": "fly",
  "pec-deck": "fly",

  "barbell-curl": "curl",
  "preacher-curl": "curl",
  "hammer-curl": "curl",

  "skull-crusher": "tricep-ext",
  "overhead-tricep-extension": "tricep-ext",
  "tricep-pushdown": "tricep-ext",

  plank: "plank",
  "hanging-leg-raise": "hang-knee",
  "russian-twist": "twist",
  "ab-wheel": "ab-wheel",

  "leg-extension": "leg-ext",
  "lying-leg-curl": "leg-curl",

  "calf-raise": "calf-raise",
};

/** Heuristic pattern lookup when we don't have an explicit map entry. */
export function patternFor(exercise) {
  if (!exercise) return "standing";
  const direct = EXERCISE_PATTERN[exercise.id];
  if (direct) return direct;
  const name = (exercise.name || "").toLowerCase();
  const muscles = (exercise.muscles || []).join(" ").toLowerCase();
  if (name.includes("squat")) return "squat";
  if (name.includes("deadlift") || name.includes("hinge") || name.includes("rdl")) return "hinge";
  if (name.includes("bench")) return "bench-press";
  if (name.includes("overhead") || name.includes("ohp")) return "overhead-press";
  if (name.includes("row")) return "row";
  if (name.includes("pull-up") || name.includes("pullup")) return "pull-up";
  if (name.includes("pulldown")) return "lat-pulldown";
  if (name.includes("push-up") || name.includes("pushup")) return "push-up";
  if (name.includes("lunge")) return "lunge";
  if (name.includes("plank")) return "plank";
  if (name.includes("curl") && muscles.includes("hamstring")) return "leg-curl";
  if (name.includes("curl")) return "curl";
  if (name.includes("extension") && muscles.includes("quad")) return "leg-ext";
  if (name.includes("tricep") || name.includes("skull")) return "tricep-ext";
  if (name.includes("lateral") || name.includes("delt")) return "lateral-raise";
  if (name.includes("fly")) return "fly";
  if (name.includes("crunch") || name.includes("sit-up") || name.includes("situp")) return "crunch";
  if (name.includes("twist")) return "twist";
  if (name.includes("calf")) return "calf-raise";
  if (name.includes("hip thrust") || name.includes("hip-thrust")) return "hip-thrust";
  return "standing";
}

export default function ExerciseFormSVG({ exercise, className = "" }) {
  const key = patternFor(exercise);
  const Cmp = PATTERNS[key] || StandingSVG;
  return <Cmp className={className} aria-hidden="true" />;
}
